import { HttpClient, httpResource } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { tap } from 'rxjs'
import { env } from '../../../environments/environment'
import {
  InvoiceApproveReq,
  InvoiceBeiblattReq,
  InvoiceOpenCounter,
  InvoiceOverview,
  InvoiceOverwriteCorrectionPositionsReq,
  VorgemerktePosition,
  VorgemerktePositionSchreiben,
  VorgemerktePositionStatus,
} from '../model/intern-backend.model'

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  readonly http = inject(HttpClient)
  readonly apiUrl = env().api.internalUrl

  readonly openInvoiceCount = httpResource<InvoiceOpenCounter>(() => `${this.apiUrl}/invoicing/open/counter`)
  readonly sentInvoiceCount = httpResource<InvoiceOpenCounter>(() => `${this.apiUrl}/invoicing/sent/counter`)
  readonly vorgemerktCount = httpResource<InvoiceOpenCounter>(() => `${this.apiUrl}/invoicing/vorgemerkt/counter`)

  getOpenInvoices$() {
    return this.http.get<InvoiceOverview[]>(`${this.apiUrl}/invoicing/open`)
  }

  getSentInvoices$() {
    return this.http.get<InvoiceOverview[]>(`${this.apiUrl}/invoicing/sent`)
  }

  getInvoicePdf$(sanitaetshausId: string, invoiceDatabaseId: string) {
    return this.http.get(`${this.apiUrl}/invoicing/download/${sanitaetshausId}/${invoiceDatabaseId}`, {
      responseType: 'blob',
    })
  }

  /**
   * Alle Rechnungen (Entwürfe und versendete) als CSV — eine Zeile je Rechnung mit Beträgen
   * netto/USt/brutto für den Finanzplan-Abgleich gegen Kontoauszug und Buchhaltung.
   */
  exportInvoicesCsv$() {
    return this.http.get(`${this.apiUrl}/invoicing/export/csv`, {
      responseType: 'blob',
    })
  }

  approveInvoice$(sanitaetshausId: string, invoiceDatabaseId: string, req: InvoiceApproveReq) {
    return this.http.put(`${this.apiUrl}/invoicing/open/approve/${sanitaetshausId}/${invoiceDatabaseId}`, req).pipe(
      tap(() => {
        this.openInvoiceCount.reload()
        this.sentInvoiceCount.reload()
      }),
    )
  }

  overwriteCorrectionPositions$(sanitaetshausId: string, invoiceDatabaseId: string, req: InvoiceOverwriteCorrectionPositionsReq) {
    return this.http.put(`${this.apiUrl}/invoicing/open/overwrite-correction-positions/${sanitaetshausId}/${invoiceDatabaseId}`, req).pipe(
      tap(() => {
        this.openInvoiceCount.reload()
        this.sentInvoiceCount.reload()
      }),
    )
  }

  /** Beiblatt zum Monat (zweite Seite der Rechnung) an einem Entwurf an- oder abwählen; der nächste Download zeigt es schon. */
  setBeiblatt$(sanitaetshausId: string, invoiceDatabaseId: string, req: InvoiceBeiblattReq) {
    return this.http.put(`${this.apiUrl}/invoicing/open/beiblatt/${sanitaetshausId}/${invoiceDatabaseId}`, req)
  }

  // ---- Merkzettel ----

  getVorgemerktePositionen$(status?: VorgemerktePositionStatus) {
    const query = status ? `?status=${status}` : ''
    return this.http.get<VorgemerktePosition[]>(`${this.apiUrl}/invoicing/vorgemerkt${query}`)
  }

  createVorgemerktePosition$(sanitaetshausId: string, req: VorgemerktePositionSchreiben) {
    return this.http
      .post<VorgemerktePosition>(`${this.apiUrl}/invoicing/vorgemerkt/${sanitaetshausId}`, req)
      .pipe(tap(() => this.vorgemerktCount.reload()))
  }

  updateVorgemerktePosition$(sanitaetshausId: string, positionId: string, req: VorgemerktePositionSchreiben) {
    return this.http.put<VorgemerktePosition>(`${this.apiUrl}/invoicing/vorgemerkt/position/${sanitaetshausId}/${positionId}`, req)
  }

  deleteVorgemerktePosition$(sanitaetshausId: string, positionId: string) {
    return this.http
      .delete(`${this.apiUrl}/invoicing/vorgemerkt/position/${sanitaetshausId}/${positionId}`)
      .pipe(tap(() => this.vorgemerktCount.reload()))
  }

  deleteInvoice$(sanitaetshausId: string, invoiceDatabaseId: string) {
    return this.http.delete(`${this.apiUrl}/invoicing/open/${sanitaetshausId}/${invoiceDatabaseId}`).pipe(
      tap(() => {
        this.openInvoiceCount.reload()
        // Vorgemerkte Positionen des Entwurfs sind jetzt wieder offen — sonst zeigt der Merkzettel
        // seinen alten Zählerstand, während die Positionen längst zurück sind.
        this.vorgemerktCount.reload()
      }),
    )
  }

  /**
   * Verwirft den unversendeten Entwurf und generiert ihn aus den aktuellen Abrechnungsdaten neu
   * (Löschen + Neuberechnung laufen serverseitig im saniup-deamon). Manuelle Korrekturen gehen dabei
   * verloren; die Rechnungsnummer kann sich ändern.
   */
  regenerateInvoice$(sanitaetshausId: string, invoiceDatabaseId: string) {
    return this.http.post(`${this.apiUrl}/invoicing/open/regenerate/${sanitaetshausId}/${invoiceDatabaseId}`, {}).pipe(
      tap(() => {
        this.openInvoiceCount.reload()
        this.vorgemerktCount.reload()
      }),
    )
  }
}
